import styled from "@emotion/styled"
import { css } from "@emotion/react"

import { fullGrid } from "css/grid"
import getMediaQuery, { media } from "css/breakpoints"
import { headline50, body50, legend80, legend100, bold, light, visuallyHidden } from "css/text"
import { buttonReset } from "css/buttons"

import { cssPrice } from "components/price/Price"
import { pageGradientVar } from "components/model/colors"

const transitionTime = "300ms"

export const Root = styled.header`
  background: linear-gradient(0deg, rgb(240, 240, 240), rgb(255, 255, 255));
  position: relative;
  z-index: 10;
`

export const ModelCover = styled.div`
  ${fullGrid}
  /* grid-area: 1/-1; */
  opacity: 1;
  position: relative;
  transition: background 800ms cubic-bezier(0.32, 0.94, 0.6, 1), color ${transitionTime} /*, opacity 800ms cubic-bezier(1, 1, 1, 1) 0ms, visibility 0ms 0ms*/;
  /* visibility: visible; */
  /* will-change: opacity, visibility; */
  z-index: 1;
  pointer-events: none;

  height: ${({ headerHeight }) => `calc(100svh - var(${headerHeight}))`};
  min-height: 400px;

  & button {
    pointer-events: auto;
  }

  /* &.wv_hide {
    opacity: 0;
    visibility: hidden;
  } */

  @media ${media.portrait} {
    grid-template-rows: 80px [picture] minmax(0, 50vh) [edito] auto [bottom-row] minmax(50px, auto);
  }

  @media ${media.landscape} {
    grid-template-rows: 80px [picture edito] minmax(200px, 70vh) [bottom-row] 80px;
  }

  &.dark-theme {
    background: black;
  }
`

export const NewModelWrapper = styled.div`
  align-self: self-start;
  grid-column: main;
  grid-row: picture;

  @media ${media.landscape} {
    align-self: self-end;
    grid-row: bottom-row;
    margin-block: 0;
  }

  .light-theme & .wishlist-button {
    // to override default button style in that case
    color: rgba(var(--green)) !important;

    svg {
      fill: rgba(var(--green)) !important;
    }

    @media (hover: hover) {
      &:hover {
        color: rgba(var(--light-black)) !important;

        svg {
          fill: rgba(var(--light-black)) !important;
        }
      }
    }
  }

  .wishlist-button-root {
    ${props =>
      props.mobile &&
      `display: block;

    ${getMediaQuery("m")} {
      display: none;
    }
    `}

    ${props =>
      !props.mobile &&
      `display: none;

    ${getMediaQuery("m")} {
      display: block;
    }
    `}
  }
`

export const New = styled.p`
  ${legend100}
  ${bold}
`

export const Textblock = styled.div`
  align-self: center;
  grid-column: main;
  grid-row: edito;

  @media ${media.landscape} {
    grid-column: col 1 / span 4;
    grid-row: edito;
  }

  /* padding-block-end: 1.875rem; */
  .${cssPrice} {
    position: relative;
    min-height: 1.4375rem;
  }
`

export const HGroup = styled.div`
  display: grid;
  /* row-gap: 1rem; */
  /* margin-block-start: 1rem; */

  ${getMediaQuery("m")} {
    margin-block-start: 1rem;
  }
`

export const H1 = styled.h1`
  display: contents;
`

export const Title = styled.p`
  /* ${headline50} */
  font-size: clamp(1.375rem, 1.25rem + 0.3125vw, 3.125rem); // 22px > 50px
  line-height: 1.1;
  margin-bottom: 1rem;

  ${getMediaQuery("s", { max: true })} {
    margin-top: 2rem;
    margin-bottom: 0.5rem;
  }
`

export const Baseline = styled.p`
  ${headline50}
  background: var(${pageGradientVar});
  background-clip: text;
  grid-row: 2;
  line-height: 1.1;
  margin-block-end: 1rem;
  padding-block-end: 0.1875rem;
  -webkit-text-fill-color: transparent;

  // remove gradient in dark-mode
  .dark-theme & {
    background: currentColor;
    background-clip: text;
  }

  ${getMediaQuery("s", { max: true })} {
    ${visuallyHidden}
  }
`

export const ShortSpecs = styled.p`
  /* ${body50} */
  ${light}
  font-size: clamp(0.875rem, 0.9375rem + 0.1563vw, 1.25rem); // 14px > 20px
  line-height: 1.2;
  text-wrap: balance;
  span {
    display: block;
  }
`

export const Figures = styled.div`
  grid-area: 1/-1;
  position: absolute;
  z-index: 0;
  align-self: stretch;
`

const figureStyles = css`
  /* grid-row: picture; */
  grid-row: 1 / span 2;
  grid-column: col 1 / span 6;
  align-content: center;
  margin-block-start: 10px;
  position: relative;
  top: 20px;

  ${getMediaQuery("m")} {
    grid-column: col 4 / span 6;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;

    ::selection {
      background: transparent;
    }
  }
`
export const DayFigure = styled.figure`
  ${figureStyles}
  opacity: ${props => (!!props.day ? 1 : 0.2)};
  transition: opacity ${transitionTime};
  pointer-events: auto;
  cursor: pointer;
  z-index: -1;
`
export const NightFigure = styled.figure`
  ${figureStyles}
  z-index: 1;
  opacity: ${props => (!!props.day ? 0 : 1)};
  transition: opacity ${transitionTime};
`
export const ShadowFigure = styled.figure`
  ${figureStyles}
  opacity: ${props => (!!props.day ? 0.5 : 0)};
  position: relative;
  /* top: 14.49%; */
  top: 30px;
  transition: opacity ${transitionTime};
  margin-block-start: 0;

  height: calc(100% * (1380 / 1180));
`

export const NighttimeWrapper = styled.div`
  /* align-self: center;
  grid-row: bottom-row;
  grid-column: main;
  justify-self: center; */
`

export const Options = styled.div`
  align-self: center;
  grid-row: bottom-row;
  grid-column: main;
  justify-self: center;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  width: 100%;
  justify-content: center;

  &.full {
    all: unset;
    display: grid;
    grid-template-rows: auto auto;
    grid-row: bottom-row;
    grid-column: main;
    width: 100%;
    position: relative;

    & > div {
      grid-area: 1/1;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      ${legend80}
      ${bold}
      display: flex;
      flex-direction: column;
      align-items: center;

      @media screen and (max-width: 480px) {
        p {
          display: none;
        }
      }
    }

    & > div:nth-of-type(1) {
      transform: translateX(calc(-100% - 0.5rem));
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    & > div:nth-of-type(2) {
      transform: translateX(0.5rem);
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
`

export const ShowVariations = styled.button`
  ${buttonReset}
  ${legend80}
  ${bold}
  align-items: center;
  align-self: center;
  color: rgb(var(--light-black)); // keep this one
  display: grid;
  grid-column: col 5 / span 2;
  transition: color ${transitionTime}, opacity ${transitionTime};
  max-width: 120px;

  ${getMediaQuery("s", { max: true })} {
    margin-top: 60px;
  }

  /* &:lang(ru) {
    font-size: clamp(0.5625rem, 0.4688rem + 0.2344vw, 0.75rem); // V7-2398
  } */

  ${getMediaQuery("m")} {
    grid-column: col 10 / span 2;
    min-width: 120px;
    max-width: 200px;
  }

  @media ${media.portrait} {
    /* grid-row: picture; */
    grid-row: 1 / -1;
    /* justify-self: self-end; */
    /* margin-top: 20vh; */
  }

  @media ${media.landscape} {
    grid-row: 1 / -1;
    /* margin-top: 10vh; */
  }

  > * {
    grid-area: 1/-1;
  }

  img {
    height: 100%;
    transition: transform ${transitionTime};
    width: 100%;
  }

  span {
    align-self: flex-end;
    white-space: nowrap;
  }

  @media (hover: hover) {
    :hover {
      color: rgb(var(--green));

      img {
        /* transform: scale(1.07); */

        ${props =>
          props.isPressed &&
          css`
            opacity: 0.9;
            transform: scale(0.97);
          `}
      }
    }
  }

  .no-js & {
    display: none;
  }
`
